import React, { useState } from 'react';
import { Redirect, useHistory } from 'react-router';
import { t } from '@lingui/core/macro';
import OrganizationalUnitApi from 'src/api/OrganizationalUnitApi';
import CustomBadge from 'src/components/CustomBadge';
import { Skeleton } from 'src/components/ui';
import OrgUnitProvisioningWarning, { OrgUnitProvisioningFailure } from 'src/components/OrgUnitProvisioningWarning';
import {
  EditorPage,
  SectionGrid,
  fetchOrgUnitTypes,
  scrollToFirstError,
  useEntityEditor,
  useManagedOrgUnits,
  useOrgUnitTypes,
  useUnsavedChangesGuard,
} from 'src/components/forms';
import { useOrgUnitAccess } from 'src/util/orgUnitAccess';
import { getOrgUnitTypeColor } from 'src/util/orgUnitTypeColors';
import { SYSTEM_ORG_UNIT_TYPES, allowedParentTypeIds, findTypeByName, typeBehaviour } from 'src/util/orgUnitTypes';
import useTitle from 'src/util/useTitle';
import { OrgUnitEditorContext, editorContextConfig } from './editorContext';
import { OrgUnitRecord, newOrgUnitStub, normalizeOrgUnit } from './orgUnitModel';
import { withoutBlankContacts } from './contacts';
import { orgUnitPayload } from './orgUnitPayload';
import { validateOrgUnit } from './validateOrgUnit';
import GeneralSection from './GeneralSection';
import IdentifiersSection from './IdentifiersSection';
import AddressSection from './AddressSection';
import ContactsSection from './ContactsSection';
import InstitutionSummarySection from './InstitutionSummarySection';

export type OrgUnitEditorPageProps = {
  context: OrgUnitEditorContext;
  /** 'new' to create; ignored for `mine`. */
  orgUnitId?: string;
  setOrgUnitId?: (id: string | undefined) => void;
};

const api = OrganizationalUnitApi as any;

// Rendered while the unit loads so the sections can show their layout with placeholder controls.
const LOADING_UNIT = newOrgUnitStub(null);

/**
 * One editor for every org unit, whichever page opened it: the sections follow the unit's type, and
 * what can be changed follows the viewer's org-unit permissions (full / contact info only / read-only).
 */
function OrgUnitEditorPage({ context, orgUnitId = '', setOrgUnitId }: OrgUnitEditorPageProps) {
  const history = useHistory();
  const config = editorContextConfig(context);
  const { canManage, editorMode } = useOrgUnitAccess();
  const isNew = context !== 'mine' && orgUnitId === 'new';
  const types = useOrgUnitTypes();
  const units = useManagedOrgUnits(editorMode === 'full');
  const [warnings, setWarnings] = useState<OrgUnitProvisioningFailure[] | null>(null);

  const fetchRecord = async () =>
    normalizeOrgUnit(context === 'mine' ? await api.getMyOrganizationUnit() : await api.getOrganizationUnit({ id: orgUnitId, includeAncestors: true }));

  const editor = useEntityEditor<OrgUnitRecord, OrgUnitRecord>({
    load: async () => {
      if (!isNew) return fetchRecord();
      const institution = config.lockToInstitution ? findTypeByName(await fetchOrgUnitTypes(), SYSTEM_ORG_UNIT_TYPES.institution) : undefined;
      return newOrgUnitStub(institution?.id ?? null);
    },
    save: async draft => {
      setWarnings(null);
      const response: any =
        editorMode === 'contactInfo'
          ? await api.editOrganizationUnitContactInfo({ id: draft.id, orgContactInfo: withoutBlankContacts(draft.orgContactInfo) })
          : await saveFull(draft);
      const reported = response?.data?.provisioningWarnings;
      setWarnings(Array.isArray(reported) && reported.length > 0 ? reported : null);
      // A create is reloaded through the URL switch; an update reloads here so the CFIID and path are current.
      return isNew ? normalizeOrgUnit(response?.data ?? draft) : fetchRecord();
    },
    validate: (draft, original) => validateOrgUnit(draft, { mode: editorMode, isNew, type: typeOf(draft), parent: parentOf(draft), original }),
    deps: [context, orgUnitId],
  });
  const guard = useUnsavedChangesGuard(editor.isDirty);
  const { draft } = editor;

  function typeOf(record: OrgUnitRecord | undefined) {
    return types.options.find(type => type.id === record?.organizationalUnitTypeId) ?? null;
  }

  function parentOf(record: OrgUnitRecord | undefined) {
    return units.options.find(unit => unit.id === record?.parentId) ?? null;
  }

  function saveFull(record: OrgUnitRecord) {
    const payload = orgUnitPayload(record, { isNew, typeName: typeOf(record)?.name ?? null, parentCode: parentOf(record)?.code ?? null });
    return isNew ? api.createOrganizationUnit(payload) : api.editOrganizationUnit({ id: record.id, ...payload });
  }

  const pageLoading = editor.loading || types.loading;
  const type = typeOf(draft);
  const parent = parentOf(draft);
  // The type is unknown until the unit arrives. Pages that only edit institutions show the institution
  // layout meanwhile; Organizational Units shows General alone and adds the rest if it is one.
  const behaviour = pageLoading ? { code: 'none' as const, isInstitution: context !== 'orgUnits' } : typeBehaviour(type?.name);
  const unit = draft ?? LOADING_UNIT;

  useTitle(pageTitle(context, isNew, draft?.name));

  function onTypeChange(typeId: number | null) {
    editor.setField('organizationalUnitTypeId', typeId);
    const allowed = allowedParentTypeIds(types.options.find(candidate => candidate.id === typeId));
    const parentTypeId = parent?.organizationalUnitType?.id;
    if (draft?.parentId != null && (allowed.length === 0 || parentTypeId == null || !allowed.includes(parentTypeId))) {
      editor.setField('parentId', null);
    }
  }

  async function onSave() {
    const { ok, result } = await editor.save();
    if (!ok) {
      scrollToFirstError();
      return;
    }
    if (isNew && result?.id != null && setOrgUnitId) {
      const newId = String(result.id);
      guard.leave(() => setOrgUnitId(newId));
    }
  }

  if (isNew && !canManage) return <Redirect to={config.listPath ?? '/'} />;

  const canSave = editorMode === 'full' || (editorMode === 'contactInfo' && behaviour.isInstitution);
  const listPath = config.listPath;

  return (
    <EditorPage
      breadcrumb={config.breadcrumb()}
      title={
        <span className="inline-flex items-center gap-2">
          {isNew ? newTitle(context) : pageLoading ? <Skeleton inline width="10rem" height="1rem" /> : draft?.name}
          {type && !pageLoading ? <CustomBadge color={getOrgUnitTypeColor(type.name) || 'secondary'}>{type.name}</CustomBadge> : null}
        </span>
      }
      isDirty={editor.isDirty}
      lastSavedAt={editor.lastSavedAt}
      loading={pageLoading}
      inlineLoading
      error={editor.loadError || types.error || units.error || editor.saveError}
      errorCount={editor.errorCount}
      showErrorSummary={editor.submitted}
      saving={editor.saving}
      onCancel={listPath ? () => history.push(listPath) : undefined}
      onSave={canSave ? onSave : undefined}
      saveLabel={isNew ? t`Create` : undefined}
    >
      {guard.prompt}
      <OrgUnitProvisioningWarning warnings={warnings} />
      {draft || pageLoading ? (
        <SectionGrid>
          {editorMode === 'full' ? (
            <>
              <GeneralSection
                draft={unit}
                setField={editor.setField}
                errorFor={editor.errorFor}
                types={types.options}
                type={type}
                lockType={config.lockToInstitution}
                parentLabel={config.parentLabel()}
                units={units.options}
                unitsLoading={units.loading}
                parent={parent}
                onTypeChange={onTypeChange}
                span={behaviour.isInstitution ? 7 : 12}
              />
              {behaviour.isInstitution ? <IdentifiersSection draft={unit} setField={editor.setField} errorFor={editor.errorFor} isNew={isNew} /> : null}
              {behaviour.isInstitution ? <AddressSection draft={unit} setField={editor.setField} errorFor={editor.errorFor} /> : null}
            </>
          ) : (
            <InstitutionSummarySection draft={unit} type={type} />
          )}
          {behaviour.isInstitution ? (
            <ContactsSection
              info={unit.orgContactInfo} setField={editor.setField} errorFor={editor.errorFor}
              readOnly={editorMode === 'readonly'}
              onRowsRemoved={() => editor.clearTouched('orgContactInfo.otherContacts')}
            />
          ) : null}
        </SectionGrid>
      ) : null}
    </EditorPage>
  );
}

function newTitle(context: OrgUnitEditorContext): string {
  return context === 'institutions' ? t`New Institution` : t`New Organizational Unit`;
}

function pageTitle(context: OrgUnitEditorContext, isNew: boolean, name: string | undefined): string {
  if (isNew) return newTitle(context);
  if (!name) return '';
  if (context === 'institutions') return t`Edit Institution | ${name}`;
  if (context === 'mine') return t`My Institution | ${name}`;
  return t`Edit Organizational Unit | ${name}`;
}

export default OrgUnitEditorPage;
